import Check from '@mui/icons-material/Check';
import { BaseModal } from '@src/common/components/BaseModal';
import { Divider } from '@src/common/components/Divider';
import { List, ListItem, ListItemText } from '@src/common/components/List';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { UploadFile } from '@src/documents/components/FormFileUpload';
import type { FormConditionSheet } from '@src/permits/completePermit/fields/ConditionSheets';
import { useTranslation } from 'react-i18next';

export const ConfirmDeliveryModal = ({
  statePermitDocument,
  documents,
  conditionSheets,
  open,
  onClose,
  onConfirm,
}: {
  statePermitDocument?: UploadFile;
  documents: UploadFile[];
  conditionSheets: FormConditionSheet[];
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation();

  const selectedDocuments = documents.filter(
    (document) => document.sendToClient,
  );
  const selectedConditionSheets = conditionSheets.filter(
    (sheet) => sheet.sendToClient,
  );

  const hasSelectedStatePermit = !!statePermitDocument?.sendToClient;
  const hasSelectedDocuments = selectedDocuments.length > 0;
  const hasSelectedConditionSheets = selectedConditionSheets.length > 0;
  const hasNothingSelected =
    !hasSelectedStatePermit &&
    !hasSelectedDocuments &&
    !hasSelectedConditionSheets;

  const translations = hasNothingSelected
    ? 'confirmNoDocuments'
    : 'confirmPermitDocuments';

  return (
    <BaseModal open={open} onClose={onClose} title={t(`${translations}.title`)}>
      <Stack paddingBottom={theme.spacing[4]}>
        <Typography style={{ ...theme.typography.body.medium }}>
          {t(`${translations}.description`)}
        </Typography>
      </Stack>

      {hasSelectedStatePermit && (
        <>
          <Typography
            style={{ ...theme.typography.label.large }}
            className={underlined}
          >
            {t('statePermitDocument')}
          </Typography>

          <List>
            <ListItem key={statePermitDocument.documentId}>
              <ListItemText
                inset
                primary={getUploadfileName(statePermitDocument)}
                primaryTypographyProps={{
                  sx: { fontWeight: 500 },
                }}
              />
            </ListItem>
          </List>
        </>
      )}

      {hasSelectedDocuments && (
        <>
          <Typography
            style={{ ...theme.typography.label.large }}
            className={underlined}
          >
            {t('permitDocuments')}
          </Typography>

          <List>
            {selectedDocuments.map((document) => (
              <ListItem key={document.documentId}>
                <ListItemText
                  inset
                  primary={getUploadfileName(document)}
                  primaryTypographyProps={{
                    sx: { fontWeight: 500 },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {hasSelectedConditionSheets && (
        <>
          <Typography
            style={{ ...theme.typography.label.large }}
            className={underlined}
          >
            {t('conditionsSheets')}
          </Typography>

          <List>
            {selectedConditionSheets.map((conditionSheet) => (
              <ListItem key={conditionSheet.id}>
                <ListItemText
                  inset
                  primary={conditionSheet.filePath}
                  primaryTypographyProps={{
                    sx: { fontWeight: 500 },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Divider
        style={{
          marginTop: theme.spacing['6'],
          marginBottom: theme.spacing['6'],
        }}
      />

      <CloseConfirmModalFooter
        onClose={onClose}
        confirm={onConfirm}
        confirmIcon={<Check style={{ marginRight: theme.spacing['2'] }} />}
        closeLabel={t(`${translations}.cancel`)}
        confirmLabel={t(`${translations}.confirm`)}
      />
    </BaseModal>
  );
};

const underlined = css(() => ({
  borderBottom: '1px solid',
  borderColor: theme.palette.border.primary,
}));

const getUploadfileName = (file: UploadFile) => {
  return file.displayName?.trim().length ? file.displayName : file.name;
};
